import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  IconButton,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  MenuItem as MenuItemComponent,
  Pagination,
  Alert,
  Skeleton,
  Tooltip,
  Fab
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  MoreVert as MoreVertIcon,
  FilterList as FilterIcon,
  ViewModule as ViewModuleIcon,
  Assignment as TemplateIcon,
  TrendingUp as TrendingUpIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';

import useProjectTemplateStore from '../stores/projectTemplateStore';
// import { PageContainer } from '../design-system';

const PROJECT_CATEGORIES = [
  { value: 'all', label: 'All Categories' },
  { value: 'general', label: 'General' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'development', label: 'Development' },
  { value: 'content', label: 'Content' },
  { value: 'custom', label: 'Custom' }
];

const SORT_OPTIONS = [
  { value: 'usageCount', label: 'Most Used' },
  { value: 'createdAt', label: 'Recently Created' },
  { value: 'name', label: 'Name' },
  { value: 'updatedAt', label: 'Recently Updated' }
];

// Template Card Component
const TemplateCard = ({ template, onMenuOpen, onUseTemplate, onEdit }) => (
  <Card 
    elevation={2}
    sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      transition: 'all 0.2s ease-in-out',
      '&:hover': {
        elevation: 4,
        transform: 'translateY(-2px)'
      }
    }}
  >
    <CardContent sx={{ flexGrow: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Typography variant="h6" component="h3" noWrap sx={{ flex: 1, mr: 1 }}>
          {template.name}
        </Typography>
        <IconButton
          size="small"
          onClick={(e) => onMenuOpen(e, template)}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Box>

      <Typography 
        variant="body2" 
        color="text.secondary" 
        sx={{ 
          mb: 2, 
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical'
        }}
      >
        {template.description || 'No description provided'}
      </Typography>

      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Chip 
          label={template.category} 
          size="small" 
          color="primary"
          variant="outlined"
        />
        <Chip 
          label={`${template.sections?.length || 0} sections`} 
          size="small"
          variant="outlined"
        />
        <Chip 
          label={`Used ${template.usageCount || 0} times`} 
          size="small"
          color="secondary"
          variant="outlined"
          icon={<TrendingUpIcon />}
        />
      </Box>

      <Typography variant="caption" color="text.secondary">
        Created by {template.createdBy?.firstName} {template.createdBy?.lastName}
      </Typography>
    </CardContent>

    <CardActions sx={{ justifyContent: 'space-between', pt: 0 }}>
      <Button
        size="small"
        variant="contained"
        onClick={() => onUseTemplate(template)}
        startIcon={<TemplateIcon />}
      >
        Use Template
      </Button>
      <Button
        size="small"
        variant="outlined"
        onClick={() => onEdit(template)}
        startIcon={<EditIcon />}
      >
        Edit
      </Button>
    </CardActions>
  </Card>
);

const ProjectTemplates = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  
  const {
    templates,
    isLoading,
    error,
    pagination,
    filters,
    fetchTemplates,
    deleteTemplate,
    duplicateTemplate,
    setFilters,
    clearError
  } = useProjectTemplateStore();

  const [searchTerm, setSearchTerm] = useState(filters?.search || '');
  const [deleteDialog, setDeleteDialog] = useState({ open: false, template: null });
  const [duplicateDialog, setDuplicateDialog] = useState({ open: false, template: null, name: '' });
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  // Load templates on component mount
  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  // Handle search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm !== (filters?.search || '')) {
        setFilters({ search: searchTerm });
        fetchTemplates({ page: 1 });
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm, filters?.search, setFilters, fetchTemplates]);

  // Handle filter changes
  const handleFilterChange = (field, value) => {
    setFilters({ [field]: value });
    fetchTemplates({ page: 1, [field]: value });
  };

  // Handle pagination
  const handlePageChange = (event, page) => {
    fetchTemplates({ page });
  };

  // Handle template actions
  const handleMenuOpen = (event, template) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setSelectedTemplate(template);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedTemplate(null);
  };

  const handleEdit = (template) => {
    navigate(`/project-templates/${template._id}/edit`);
    handleMenuClose();
  };

  const handleDuplicate = (template) => {
    setDuplicateDialog({
      open: true,
      template,
      name: `${template.name} (Copy)`
    });
    handleMenuClose();
  };

  const handleDelete = (template) => {
    setDeleteDialog({ open: true, template });
    handleMenuClose();
  };

  const handleUseTemplate = (template) => {
    navigate(`/projects/create?templateId=${template._id}`);
  };

  // Handle duplicate template
  const handleDuplicateConfirm = async () => {
    try {
      await duplicateTemplate(duplicateDialog.template._id, duplicateDialog.name);
      enqueueSnackbar('Template duplicated successfully', { variant: 'success' });
      setDuplicateDialog({ open: false, template: null, name: '' });
    } catch (error) {
      enqueueSnackbar('Failed to duplicate template', { variant: 'error' });
    }
  };

  // Handle delete template
  const handleDeleteConfirm = async () => {
    try {
      await deleteTemplate(deleteDialog.template._id);
      enqueueSnackbar('Template deleted successfully', { variant: 'success' });
      setDeleteDialog({ open: false, template: null });
    } catch (error) {
      enqueueSnackbar('Failed to delete template', { variant: 'error' });
    }
  };

  // Clear error when component unmounts
  useEffect(() => {
    return () => clearError();
  }, [clearError]);

  const LoadingSkeleton = () => (
    <Grid container spacing={3}>
      {[...Array(6)].map((_, index) => (
        <Grid item xs={12} sm={6} md={4} key={index}>
          <Card>
            <CardContent>
              <Skeleton variant="text" width="80%" height={32} />
              <Skeleton variant="text" width="100%" height={20} sx={{ mt: 1 }} />
              <Skeleton variant="text" width="60%" height={20} />
              <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                <Skeleton variant="rectangular" width={80} height={24} />
                <Skeleton variant="rectangular" width={100} height={24} />
              </Box>
            </CardContent>
            <CardActions>
              <Skeleton variant="rectangular" width={120} height={36} />
              <Skeleton variant="rectangular" width={80} height={36} />
            </CardActions>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Project Templates
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Manage and create reusable project structures
      </Typography>
      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={clearError}>
          {error}
        </Alert>
      )}

      {/* Filters and Search */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search templates..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={filters?.category || 'all'}
                  label="Category"
                  onChange={(e) => handleFilterChange('category', e.target.value)}
                >
                  {PROJECT_CATEGORIES.map((category) => (
                    <MenuItem key={category.value} value={category.value}>
                      {category.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={filters?.sortBy || 'usageCount'}
                  label="Sort By"
                  onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                >
                  {SORT_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate('/project-templates/create')}
                sx={{ height: 56 }}
              >
                Create Template
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Templates Grid */}
      {isLoading ? (
        <LoadingSkeleton />
      ) : (templates && templates.length > 0) ? (
        <>
          <Grid container spacing={3}>
            {(templates || []).map((template) => (
              <Grid item xs={12} sm={6} md={4} key={template._id}>
                <TemplateCard 
                  template={template} 
                  onMenuOpen={handleMenuOpen}
                  onUseTemplate={handleUseTemplate}
                  onEdit={handleEdit}
                />
              </Grid>
            ))}
          </Grid>

          {/* Pagination */}
          {(pagination?.totalPages || 0) > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Pagination
                count={pagination?.totalPages || 1}
                page={pagination?.currentPage || 1}
                onChange={handlePageChange}
                color="primary"
                size="large"
              />
            </Box>
          )}
        </>
      ) : (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <ViewModuleIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No templates found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {(filters?.search) || (filters?.category && filters.category !== 'all')
              ? 'Try adjusting your filters or search terms'
              : 'Create your first project template to get started'
            }
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/project-templates/create')}
          >
            Create Template
          </Button>
        </Box>
      )}

      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        onClick={(e) => e.stopPropagation()}
      >
        <MenuItem onClick={() => handleEdit(selectedTemplate)}>
          <EditIcon sx={{ mr: 2 }} />
          Edit
        </MenuItem>
        <MenuItem onClick={() => handleDuplicate(selectedTemplate)}>
          <CopyIcon sx={{ mr: 2 }} />
          Duplicate
        </MenuItem>
        <MenuItem onClick={() => handleDelete(selectedTemplate)}>
          <DeleteIcon sx={{ mr: 2 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, template: null })}
      >
        <DialogTitle>Delete Template</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{deleteDialog.template?.name}"? 
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, template: null })}>
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Duplicate Dialog */}
      <Dialog
        open={duplicateDialog.open}
        onClose={() => setDuplicateDialog({ open: false, template: null, name: '' })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Duplicate Template</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Template Name"
            value={duplicateDialog.name}
            onChange={(e) => setDuplicateDialog(prev => ({ ...prev, name: e.target.value }))}
            margin="normal"
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDuplicateDialog({ open: false, template: null, name: '' })}>
            Cancel
          </Button>
          <Button 
            onClick={handleDuplicateConfirm} 
            variant="contained"
            disabled={!duplicateDialog.name.trim()}
          >
            Duplicate
          </Button>
        </DialogActions>
      </Dialog>

      {/* Floating Action Button */}
      <Fab
        color="primary"
        aria-label="create template"
        onClick={() => navigate('/project-templates/create')}
        sx={{
          position: 'fixed',
          bottom: 16,
          right: 16,
          display: { xs: 'flex', md: 'none' }
        }}
      >
        <AddIcon />
      </Fab>
    </Box>
  );
};

export default ProjectTemplates;